import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import MiniDrawer from "../../../components/MiniDrawer/MiniDrawer";
import { useNavigate, useLocation } from "react-router-dom";
import { DateRangePicker } from "react-date-range";
import * as XLSX from "xlsx";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";


/* ─── helpers ─────────────────────────────────────────────── */




/** Returns the 1st April of the current financial year as a JS Date */
function getFinancialYearStart() {
    const today = new Date();
    const year = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    return new Date(year, 3, 1); // April = month index 3
}

/** Format a Date object → "YYYY-MM-DD" for the API */
function toAPIDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

/* ─── component ───────────────────────────────────────────── */

const EmployeeExpenseDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const baseAPI = process.env.REACT_APP_API_URL;

    const employee_id = location.state?.customer_id ?? "";

    /* --- raw data & display data --- */
    const [allData, setAllData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [employeeDetails, setEmployeeDetails] = useState(null);


    /* --- date range state --- */
    const [dateRange, setDateRange] = useState([
        {
            startDate: getFinancialYearStart(),
            endDate: new Date(),
            key: "selection",
        },
    ]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const datePickerRef = useRef(null);


    const [transactionType, setTransactionType] = useState("All");

    /* --- pagination --- */
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    /* ── fetch data ──────────────────────────────────────────── */
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]);

    /* close date-picker on outside click */
    useEffect(() => {
        const handler = (e) => {
            if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
                setShowDatePicker(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    function fetchData() {
        const storedToken = JSON.parse(localStorage.getItem("token"));
        if (!storedToken) return;

        const fromDate = toAPIDate(dateRange[0].startDate);
        const toDate = toAPIDate(dateRange[0].endDate);
        const headers = { Authorization: `Bearer ${storedToken}` };

        axios
            .get(
                `${baseAPI}userget/GetPettyCashDetailReport?employee_id=${employee_id}&from_date=${fromDate}&to_date=${toDate}`,
                { headers }
            )
            .then((res) => {
                const data = res.data.data ?? [];
                setAllData(data);
                applyTransactionFilter(transactionType, data);
            })
            .catch((err) => console.error("Error fetching petty cash detail:", err));
    }

    /* ── amount filter ───────────────────────────────────────── */
    function applyTransactionFilter(type = transactionType, source = allData) {
        let result = [...source];

        if (type === "Received") {
            result = result.filter(
                (r) =>
                    r.CashReceived &&
                    Number(r.CashReceived) > 0
            );
        }

        if (type === "Expense") {
            result = result.filter(
                (r) =>
                    r.ExpenseAmount &&
                    Number(r.ExpenseAmount) > 0
            );
        }

        setFilteredData(result);
        setCurrentPage(1);
    }



    function handleClearFilters() {
        setTransactionType("All");

        setDateRange([
            {
                startDate: getFinancialYearStart(),
                endDate: new Date(),
                key: "selection",
            },
        ]);

        setFilteredData(allData);
        setCurrentPage(1);
    }

    useEffect(() => {
        if (employee_id) {
            fetchEmployeeDetails();
        }
    }, [employee_id]);

    function fetchEmployeeDetails() {
        const storedToken = JSON.parse(localStorage.getItem("token"));
        if (!storedToken) return;

        axios
            .get(
                `${baseAPI}adminget/GETEmployee?employeeID=${employee_id}&desg_id=0&dept_id=0&region_id=0&meal_Preference=&status=&location=`,
                {
                    headers: {
                        Authorization: `Bearer ${storedToken}`,
                    },
                }
            )
            .then((response) => {
                const employee = response?.data?.data?.[0] || null;
                setEmployeeDetails(employee);
            })
            .catch((error) => {
                console.error("Error fetching employee details:", error);
            });
    }



    /* ── excel export ────────────────────────────────────────── */
    function exportToExcel() {
        const exportData = filteredData.map((expense, idx) => ({
            "S No.": idx + 1,
            Date: expense.Date ?? "",
            "Lead ID": expense.LeadID ?? "",
            Vendor: expense.Vendor ?? "",
            "Cash Received (₹)": expense.CashReceived ?? "",
            "Expense Description": expense.ExpenseDescription ?? "",
            "Expense Amount (₹)": expense.ExpenseAmount ?? "",
            "Current Balance (₹)": expense.CurrentBalance ?? "",
            Remark: expense.Remark ?? "",
            Status: expense.Status ?? "",
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "PettyCash");

        const fromDate = toAPIDate(dateRange[0].startDate);
        const toDate = toAPIDate(dateRange[0].endDate);
        XLSX.writeFile(wb, `PettyCash_${fromDate}_to_${toDate}.xlsx`);
    }

    /* ── pagination helpers ──────────────────────────────────── */
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const paginatedData = filteredData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    /* ── date range label ────────────────────────────────────── */
    const dateLabel = `${toAPIDate(dateRange[0].startDate)}  →  ${toAPIDate(dateRange[0].endDate)}`;

    /* ─────────────────────────────────────────────────────────── */
    return (
        <div>
            <div className="app-container-viewLeadDetails">
                <MiniDrawer />

                <div style={{ overflowX: "hidden" }} className="container-fluid container-viewLeadDetails">

                    {/* ── Breadcrumb ── */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "1rem",
                        }}
                        className="country-header"
                    >
                        <h1 style={{ fontSize: "24px" }}>
                            <b>Statement of Accounts</b> | <HomeOutlinedIcon /> &gt;
                            <a style={{ cursor: "pointer" }} onClick={() => navigate("")}>
                                {" "}Accounts &amp; Finance{" "}
                            </a>
                            <span style={{ fontSize: "20px" }}>&gt; Reports</span>
                            <span style={{ fontSize: "20px" }}>&gt; Statement</span>
                        </h1>
                    </div>

                    <div className="container-fluid px-3 py-2">
                        {/* ── Employee Info Card ── */}
                        {employeeDetails && (
                            <div
                                style={{
                                    background: "linear-gradient(135deg, #f9f7ff 0%, #ffffff 100%)",
                                    border: "1px solid #e2daf8",
                                    borderRadius: "12px",
                                    padding: "18px 22px",
                                    marginBottom: "18px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: "16px",
                                    boxShadow: "0 2px 8px rgba(91,72,157,0.08)",
                                }}
                            >
                                {/* Left */}
                                <div>
                                    <div
                                        style={{
                                            fontSize: "22px",
                                            fontWeight: "700",
                                            color: "#2d1f69",
                                            marginBottom: "6px",
                                        }}
                                    >
                                        {employeeDetails?.name || "N/A"}
                                    </div>

                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "18px",
                                            flexWrap: "wrap",
                                            fontSize: "14px",
                                            color: "#666",
                                        }}
                                    >
                                        <span>
                                            <strong style={{ color: "#5B489D" }}>Employee Code:</strong>{" "}
                                            {employeeDetails?.emp_code || "-"}
                                        </span>

                                        <span>
                                            <strong style={{ color: "#5B489D" }}>Designation:</strong>{" "}
                                            {employeeDetails?.designation || "-"}
                                        </span>
                                    </div>
                                </div>

                                {/* Right Badge */}
                                <div
                                    style={{
                                        backgroundColor: "#5B489D",
                                        color: "#fff",
                                        padding: "10px 18px",
                                        borderRadius: "30px",
                                        fontWeight: "600",
                                        fontSize: "13px",
                                        letterSpacing: "0.5px",
                                    }}
                                >
                                    Employee Statement
                                </div>
                            </div>
                        )}

                        <div
                            style={{
                                marginTop: "14px",
                                padding: "16px 20px",
                                border: "1px solid #e0d8f8",
                                borderRadius: "8px",
                                backgroundColor: "#f9f7ff",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-end",
                                flexWrap: "wrap",
                                gap: "20px",
                            }}
                        >

                            {/* LEFT SIDE */}
                            <div
                                style={{
                                    display: "flex",
                                    gap: "20px",
                                    alignItems: "flex-end",
                                    flexWrap: "wrap",
                                }}
                            >

                                {/* DATE RANGE PICKER */}
                                <div style={{ position: "relative" }} ref={datePickerRef}>

                                    <label
                                        style={{
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            color: "#5B489D",
                                            display: "block",
                                            marginBottom: "4px",
                                        }}
                                    >
                                        Date Range
                                    </label>

                                    <button
                                        onClick={() => setShowDatePicker((v) => !v)}
                                        style={{
                                            width: "14rem",
                                            height: "2.5rem",
                                            borderRadius: "0.5rem",
                                            fontSize: "13px",
                                            border: "1px solid #5B489D",
                                            backgroundColor: "#fff",
                                            color: "#5B489D",
                                            cursor: "pointer",
                                            fontWeight: "600",
                                        }}
                                    >
                                        📅 {dateLabel}
                                    </button>

                                    {showDatePicker && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: "48px",
                                                left: "0",
                                                zIndex: 9999,
                                                border: "1px solid #dcdcdc",
                                                borderRadius: "8px",
                                                background: "white",
                                                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                                                overflow: "visible",
                                                width: "auto",
                                            }}
                                        >

                                            <DateRangePicker
                                                ranges={dateRange}
                                                onChange={(item) => setDateRange([item.selection])}
                                                moveRangeOnFirstSelection={false}
                                                months={1}
                                                direction="vertical"
                                                rangeColors={["#5B489D"]}
                                            />

                                            {/* Footer Buttons */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "flex-end",
                                                    gap: "10px",
                                                    padding: "12px",
                                                    borderTop: "1px solid #eee",
                                                    backgroundColor: "#fff",
                                                }}
                                            >

                                                {/* RESET BUTTON */}
                                                <button
                                                    onClick={() => {
                                                        setDateRange([
                                                            {
                                                                startDate: getFinancialYearStart(),
                                                                endDate: new Date(),
                                                                key: "selection",
                                                            },
                                                        ]);
                                                    }}
                                                    style={{
                                                        width: "6rem",
                                                        height: "2.5rem",
                                                        borderRadius: "0.5rem",
                                                        fontSize: "13px",
                                                        color: "red",
                                                        backgroundColor: "#fff",
                                                        border: "1px solid #ddd",
                                                        cursor: "pointer",
                                                        fontWeight: "600",
                                                    }}
                                                >
                                                    Reset
                                                </button>

                                                {/* DONE BUTTON */}
                                                <button
                                                    onClick={() => setShowDatePicker(false)}
                                                    style={{
                                                        width: "7rem",
                                                        height: "2.5rem",
                                                        borderRadius: "0.5rem",
                                                        fontSize: "13px",
                                                        backgroundColor: "#5B489D",
                                                        color: "white",
                                                        border: "none",
                                                        cursor: "pointer",
                                                        fontWeight: "600",
                                                    }}
                                                >
                                                    Done
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* TRANSACTION TYPE */}
                                <div>
                                    <label
                                        style={{
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            color: "#5B489D",
                                            display: "block",
                                            marginBottom: "4px",
                                        }}
                                    >
                                        Transaction Type
                                    </label>

                                    <select
                                        value={transactionType}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setTransactionType(value);
                                            applyTransactionFilter(value);
                                        }}
                                        style={{
                                            padding: "8px 12px",
                                            border: "1px solid #ccc",
                                            borderRadius: "5px",
                                            fontSize: "13px",
                                            minWidth: "180px",
                                            outline: "none",
                                            height: "2.5rem",
                                        }}
                                    >
                                        <option value="All">All</option>
                                        <option value="Received">Received</option>
                                        <option value="Expense">Expense</option>
                                    </select>
                                </div>

                                {/* CLEAR BUTTON */}
                                <div>
                                    <button
                                        onClick={handleClearFilters}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            padding: "8px 14px",
                                            border: "1px solid #ccc",
                                            borderRadius: "5px",
                                            backgroundColor: "#fff",
                                            cursor: "pointer",
                                            fontWeight: "600",
                                            fontSize: "13px",
                                            height: "2.5rem",
                                        }}
                                    >
                                        <CloseIcon style={{ fontSize: "14px" }} />
                                        Clear
                                    </button>
                                </div>

                            </div>

                            {/* RIGHT SIDE EXPORT BUTTON */}
                            <div>
                                <button
                                    onClick={exportToExcel}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        backgroundColor: "#217346",
                                        color: "white",
                                        border: "none",
                                        padding: "8px 14px",
                                        borderRadius: "5px",
                                        cursor: "pointer",
                                        fontWeight: "600",
                                        height: "2.5rem",
                                    }}
                                >
                                    <FileDownloadIcon style={{ fontSize: "16px" }} />
                                    Export to Excel
                                </button>
                            </div>

                        </div>

                        <hr style={{ marginTop: "20px", marginBottom: "24px", padding: 0 }} />

                        {/* ── Table ── */}
                        <div className="expenseList">
                            <h2
                                className="Venderheading"
                                style={{ fontSize: "22px", paddingLeft: "16px", paddingTop: "7px" }}
                            >
                                Expense Detail
                                <span style={{ fontSize: "14px", fontWeight: "normal", color: "#888", marginLeft: "10px" }}>
                                    ({filteredData.length} records)
                                </span>
                            </h2>

                            <table style={{ width: "100%" }} className="table_expense">
                                <thead>
                                    <tr className="expenseListHeading">
                                        <th>S No.</th>
                                        <th>Date</th>
                                        <th>Lead ID</th>
                                        <th>Vendor</th>
                                        <th>Cash Received</th>
                                        <th>Expense Description</th>
                                        <th>Expense Amount</th>
                                        <th>Current Balance</th>
                                        <th>Remark</th>
                                        <th>Status</th>
                                        <th style={{ textAlign: "center" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedData.length > 0 ? (
                                        paginatedData.map((expense, index) => (
                                            <tr key={(currentPage - 1) * rowsPerPage + index + 1}>
                                                <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                                                <td>{expense.Date ?? "-"}</td>
                                                <td>{expense.LeadID ?? "-"}</td>
                                                <td>{expense.Vendor ?? "-"}</td>
                                                <td>{expense.CashReceived ?? "-"}</td>
                                                <td>{expense.ExpenseDescription ?? "-"}</td>
                                                <td style={{ color: expense.ExpenseAmount ? "inherit" : "gray" }}>
                                                    {expense.ExpenseAmount ?? "-"}
                                                </td>
                                                <td
                                                    style={{
                                                        color: expense.CurrentBalance < 0 ? "red" : "#28a745",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    ₹ {expense.CurrentBalance ?? 0}
                                                </td>
                                                <td>{expense.Remark ?? "-"}</td>
                                                <td>
                                                    <span
                                                        style={{
                                                            backgroundColor:
                                                                expense.Status === "APPROVED" ? "#28a745" : "#dc3545",
                                                            color: "white",
                                                            padding: "3px 8px",
                                                            borderRadius: "4px",
                                                            fontSize: "12px",
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {expense.Status ?? "-"}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    <button
                                                        style={{
                                                            border: "none",
                                                            width: "37px",
                                                            padding: "0px",
                                                            height: "36px",
                                                            margin: "6px",
                                                            backgroundColor: "#F1F1F1",
                                                            color: "#6a6a6a",
                                                            cursor: "pointer",
                                                            borderRadius: "4px",
                                                        }}
                                                    >
                                                        <SearchIcon />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="11" style={{ textAlign: "center", padding: "24px", color: "#888" }}>
                                                No expenses available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>

                            {/* ── Pagination ── */}
                            {totalPages > 1 && (
                                <nav
                                    aria-label="Page navigation"
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        marginTop: "1rem",
                                        gap: "4px",
                                    }}
                                >
                                    <button
                                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                        disabled={currentPage === 1}
                                        style={pageNavBtnStyle(currentPage === 1)}
                                    >
                                        Previous
                                    </button>

                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            style={{
                                                ...pageNavBtnStyle(false),
                                                backgroundColor: page === currentPage ? "#5B489D" : "#fff",
                                                color: page === currentPage ? "#fff" : "#5B489D",
                                                border: page === currentPage ? "none" : "1px solid #dee2e6",
                                                fontWeight: page === currentPage ? "bold" : "normal",
                                            }}
                                        >
                                            {page}
                                        </button>
                                    ))}

                                    <button
                                        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        style={pageNavBtnStyle(currentPage === totalPages)}
                                    >
                                        Next
                                    </button>
                                </nav>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};



const clearBtnStyle = {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "7px 12px",
    border: "1px solid #ccc",
    borderRadius: "5px",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
};

const pageNavBtnStyle = (disabled) => ({
    padding: "6px 12px",
    border: "1px solid #dee2e6",
    borderRadius: "5px",
    backgroundColor: disabled ? "#f5f5f5" : "#fff",
    color: disabled ? "#aaa" : "#5B489D",
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: "600",
    fontSize: "13px",
});

export default EmployeeExpenseDetail;
